import { getAnalytics, logEvent } from 'firebase/analytics';
import { getAuth } from 'firebase/auth';
import { useCallback } from 'react';
import { EventSchemas } from '../analytics/events';
import {
  AnalyticsEventName,
  AnalyticsEventProperties,
} from '../types/analytics';
import {
  identifyUser as identifyHotjarUser,
  trackEvent as trackHotjarEvent,
} from '../utils/hotjar';

const mapToFirebaseEvent = (eventName: AnalyticsEventName): string => {
  return eventName.toLowerCase().replace(/\s+/g, '_');
};

export const useAnalytics = () => {
  const analytics = getAnalytics();
  const auth = getAuth();

  const validateEventProperties = useCallback(
    (
      eventName: AnalyticsEventName,
      properties?: AnalyticsEventProperties<AnalyticsEventName>,
    ) => {
      const schema = EventSchemas[eventName];
      if (!schema) {
        console.warn(`No schema found for event: ${eventName}`);
        return;
      }

      Object.entries(schema.properties).forEach(([key, config]) => {
        if (config.required && !properties?.[key]) {
          console.warn(
            `Missing required property "${key}" for event "${eventName}"`,
          );
        }
      });
    },
    [],
  );

  const trackErrorExperienced = useCallback(
    ({ error, message }: { error: string; message: string }) => {
      track('Error Experienced', {
        errorId: error,
        message: message,
      });
    },
    [],
  );

  const trackFormSubmitted = useCallback(
    ({ formName, success }: { formName: string; success?: boolean }) => {
      track('Form Submitted', {
        formName: formName,
        success: success,
      });
    },
    [],
  );

  const trackUserCreated = useCallback(
    ({ signupMethod, email }: { signupMethod: string; email?: string }) => {
      track('User Created', {
        signupMethod: signupMethod,
        email: email,
      });
    },
    [],
  );

  const track = useCallback(
    (
      eventName: AnalyticsEventName,
      properties?: AnalyticsEventProperties<AnalyticsEventName>,
    ) => {
      validateEventProperties(eventName, properties);

      trackHotjarEvent(eventName, properties);
      logEvent(analytics, mapToFirebaseEvent(eventName), properties);
    },
    [analytics, validateEventProperties],
  );

  const trackWithMethods = {
    pageViewed: useCallback(
      ({
        path,
        title,
        referrer,
      }: {
        path: string;
        title: string;
        referrer?: string;
      }) => {
        trackHotjarEvent('Page Viewed', {
          path,
          title,
          referrer,
        });
      },
      [],
    ),
    errorExperienced: trackErrorExperienced,
    formSubmitted: trackFormSubmitted,
    userCreated: trackUserCreated,
  };

  const identify = useCallback(
    (userId: string, traits: Record<string, any>) => {
      identifyHotjarUser(userId, traits);
    },
    [],
  );

  return {
    track: trackWithMethods,
    identify,
  };
};
